import { useRouter } from '@ucheba/utils/hooks/useRouter'
import React, { useCallback, useMemo } from 'react'
import { stringifyUrl, excludeFromUrl } from '@ucheba/utils/helpers/router'
import Link from 'next/link'
import { coreQuery } from '@ucheba/utils/constants/queries'
import { Button } from '../Button/desktop'
import { IUsePaginationCore } from './types'
import SC from './base/Pagination.styles'

const { Dots } = SC

export const usePaginationCore: IUsePaginationCore = (props) => {
  const {
    currentPage,
    changePageHandler,
    totalPages,
    excludeQuery = [],
    getHrefWithPage,
    shallow = false,
  } = props

  const router = useRouter()

  const hrefAsWithPage = useCallback(
    (page) => {
      if (router.query[excludeQuery[0]]) {
        return excludeFromUrl(
          stringifyUrl(router.asPath, {
            update: {
              [coreQuery.page]: page,
            },
          }),
          excludeQuery
        )
      }

      return stringifyUrl(router.asPath, {
        update: {
          [coreQuery.page]: page,
        },
      })
    },
    [excludeQuery, router.asPath, router.query]
  )

  const changeCurrentPage = useCallback(
    (number) => () => changePageHandler(number),
    [changePageHandler]
  )

  const getItems = useCallback(
    (startNumber: number, endNumber: number): any => {
      const targetItems: JSX.Element[] = []

      for (let number = startNumber; number <= endNumber; number++) {
        targetItems.push(
          <Link
            key={number}
            href={getHrefWithPage ? getHrefWithPage(number) : hrefAsWithPage(number)}
            passHref
            shallow={shallow}
            legacyBehavior
          >
            <Button
              color={number === currentPage ? 'blue' : 'gray25'}
              icon
              view='outline'
              size='small'
              onClick={changeCurrentPage(number)}
            >
              {number}
            </Button>
          </Link>
        )
      }

      return targetItems
    },
    [changeCurrentPage, currentPage, hrefAsWithPage, getHrefWithPage]
  )

  const itemsPagination = useMemo(() => {
    let items: JSX.Element[] = []

    if (totalPages <= 6) {
      items = getItems(1, totalPages)
    } else if (currentPage <= 2) {
      items = getItems(1, 3)
      items.push(<Dots key='dots-1'>...</Dots>)
      items = items.concat(getItems(totalPages, totalPages))
    } else if (currentPage > 2 && currentPage < totalPages - 2) {
      items = getItems(1, 1)
      items.push(<Dots key='dots-2'>...</Dots>)
      items = items.concat(getItems(currentPage - 1, currentPage - 1))
      items = items.concat(getItems(currentPage, currentPage))
      items = items.concat(getItems(currentPage + 1, currentPage + 1))
      items.push(<Dots key='dots-3'>...</Dots>)
      items = items.concat(getItems(totalPages, totalPages))
    } else {
      items = getItems(1, 1)
      items.push(<Dots key='dots-5'>...</Dots>)
      items = items.concat(getItems(totalPages - 2, totalPages))
    }

    return items
  }, [currentPage, getItems, totalPages])

  return {
    hrefAsWithPage,
    itemsPagination,
    changeCurrentPage,
  }
}
